import { useEffect, useRef, useState } from 'react';
import { Building2, Check, ChevronDown, Layers, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTenantScope } from '../context/TenantScopeContext';

// Selettore del tenant accanto al titolo di pagina (26/10/2026, richiesta dell'utente con
// screenshot): nella barra scura in alto si sovrapponeva a logo, ricerca e campanella. È lo
// stesso stato del "Viewing" della sidebar. `console` per le pagine a tema scuro (Overview, SOC Ops).
// Dropdown con ricerca al posto della <select> nativa (27/10/2026, da mockup dell'utente).
export default function TenantSelect({ variant = 'light' }: { variant?: 'light' | 'console' }) {
  const { user } = useAuth();
  const { tenants, selectedTenantId, setSelectedTenantId, selectedTenantName } = useTenantScope();
  const locked = user?.scopeLevel === 'tenant';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const dark = variant === 'console';
  const c = dark
    ? { trigger: 'border-[#23314d] bg-[#151f33] hover:bg-[#1c2942] text-[#e8edf7]', panel: 'border-[#23314d] bg-[#151f33]',
        searchBar: 'border-[#23314d] bg-[#101726]', input: 'text-[#e8edf7] placeholder-[#8a9cb9]', muted: 'text-[#8a9cb9]',
        divide: 'divide-[#23314d]/40', item: 'text-[#cbd7ea] hover:bg-[#1c2942]', on: 'bg-sky-500/10 text-sky-400', icon: 'text-sky-400' }
    : { trigger: 'border-gray-200 bg-white hover:bg-gray-50 text-gray-800', panel: 'border-gray-200 bg-white',
        searchBar: 'border-gray-200 bg-gray-50', input: 'text-gray-800 placeholder-gray-500', muted: 'text-gray-500',
        divide: 'divide-gray-100', item: 'text-gray-700 hover:bg-gray-50', on: 'bg-sky-50 text-sky-700', icon: 'text-sky-600' };

  const label = selectedTenantName ?? 'All tenants';
  const term = search.trim().toLowerCase();
  const visible = tenants.filter(t => t.name.toLowerCase().includes(term));
  const showAll = !term || 'all tenants'.includes(term);

  function pick(id: number | null) { setSelectedTenantId(id); setOpen(false); setSearch(''); }

  // Spostato a destra rispetto al titolo (ml-*): da solo, accanto al testo, si perdeva nella riga.
  return (
    <div className="relative inline-block text-left ml-4 sm:ml-10" ref={ref}>
      <button type="button" disabled={locked} onClick={() => setOpen(v => !v)} aria-label="Tenant" aria-haspopup="listbox" aria-expanded={open}
        title="Tenant shown in this page"
        className={`flex items-center gap-2.5 px-3.5 py-2 rounded-lg border text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500/50 disabled:opacity-80 ${locked ? 'cursor-default' : 'cursor-pointer'} ${c.trigger}`}>
        <Building2 className={`w-4 h-4 shrink-0 ${c.icon}`} />
        <span className="max-w-[14rem] truncate">{label}</span>
        {!locked && <ChevronDown className={`w-4 h-4 ${c.muted} transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />}
      </button>

      {open && (
        <div className={`absolute left-0 mt-2 w-72 rounded-xl border shadow-xl z-50 overflow-hidden ${c.panel}`}>
          <div className={`p-2 border-b flex items-center gap-2 ${c.searchBar}`}>
            <Search className={`w-4 h-4 shrink-0 ml-1 ${c.muted}`} />
            <input type="text" autoFocus placeholder="Cerca tenant..." value={search} onChange={e => setSearch(e.target.value)}
              className={`w-full bg-transparent text-xs focus:outline-none ${c.input}`} />
          </div>
          <div role="listbox" className={`max-h-60 overflow-y-auto py-1 divide-y ${c.divide}`}>
            {!showAll && visible.length === 0 && <div className={`px-3 py-2 text-xs ${c.muted}`}>Nessun tenant trovato</div>}
            {showAll && (
              <Option selected={selectedTenantId === null} cls={c} onClick={() => pick(null)}
                icon={<Layers className={`w-3.5 h-3.5 shrink-0 ${c.muted}`} />} name="All tenants" />
            )}
            {visible.map(t => (
              <Option key={t.id} selected={selectedTenantId === t.id} cls={c} onClick={() => pick(t.id)}
                icon={<Building2 className={`w-3.5 h-3.5 shrink-0 ${c.muted}`} />} name={t.name} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Option({ selected, cls, onClick, icon, name }: {
  selected: boolean; cls: { item: string; on: string; icon: string }; onClick: () => void; icon: React.ReactNode; name: string;
}) {
  return (
    <button type="button" role="option" aria-selected={selected} onClick={onClick}
      className={`w-full flex items-center justify-between px-3 py-2 text-xs transition-colors text-left ${selected ? `${cls.on} font-semibold` : cls.item}`}>
      <span className="flex items-center gap-2 truncate">{icon}<span className="truncate">{name}</span></span>
      {selected && <Check className={`w-3.5 h-3.5 shrink-0 ${cls.icon}`} />}
    </button>
  );
}
